import { useCallback } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  useRequestWithFeedback,
  type OverrideRunProps,
} from "../../../composites";
import { useLibrary } from "../../../providers";

import RequestAPI, {
  type CheckDetailPatchPayload,
  type CheckDetailPatchResponse,
} from "./api";

export const useGetTransactionImages = () => {
  const t = useLibrary("translations");
  const { send, loading, status, errorMessage, successMessage, data } =
    useRequestWithFeedback<API.TransactionImageResponse>();

  const fetchTransactions = useCallback(
    (
      transactionId: string,
      overrideProps?: OverrideRunProps<API.TransactionImageResponse>,
    ) => {
      return send({
        action: RequestAPI.getTransactionImages(transactionId),
        onData: (response_data) => {
          overrideProps?.onData?.(response_data);
        },
        messaging: {
          toast: {
            error: t.getString(
              "transaction-image-toast-error",
              {},
              "Unable to load transaction images.",
            ),
          },
        },
        ...overrideProps,
      });
    },
    [send, t],
  );

  return useObjectMemo({
    data: data.check_images ?? [],
    loading,
    error: status === "error",
    errorMessage,
    successMessage,
    send: fetchTransactions,
  });
};

export const usePatchCheckDetail = () => {
  const t = useLibrary("translations");
  const { send, loading, status, errorMessage, data } =
    useRequestWithFeedback<CheckDetailPatchResponse>();

  const patchCheckDetail = useCallback(
    (
      transactionId: string,
      checkDetailUuid: string,
      updateData: CheckDetailPatchPayload,
      overrideProps?: OverrideRunProps<CheckDetailPatchResponse>,
    ) => {
      return send({
        action: RequestAPI.patchCheckDetail(
          transactionId,
          checkDetailUuid,
          updateData,
        ),
        messaging: {
          toast: {
            error: t.getString(
              "transaction-check-detail-update-error",
              {},
              "Unable to update check details.",
            ),
            success: t.getString(
              "transaction-check-detail-update-success",
              {},
              "Check details updated.",
            ),
          },
        },
        ...overrideProps,
      });
    },
    [send, t],
  );

  return useObjectMemo({
    data,
    loading,
    error: status === "error",
    errorMessage,
    send: patchCheckDetail,
  });
};
